import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Flex,
  FlexProps,
  Hide,
  IconDownArrow,
} from "@catchoftheday/cg-components";
import { styled, themeGet } from "@catchoftheday/theme";

import { Shop } from "@header/types";
import { localStorage } from "@header/utilities";

import { scrollTo } from "../utilities/scrollTo";

import { SCROLL_BUFFER, SCROLL_INCREMENT } from "./consts";
import { MenuBar } from "./MenuBar";

type DesktopNavProps = {
  shops: Shop[];
};

type ScrollIndicatorProps = {
  placement: "left" | "right";
  visible: boolean;
};

const ScrollIndicator = styled(Flex)<ScrollIndicatorProps>`
  width: 80px;
  background: linear-gradient(
    ${(props) => (props.placement === "right" ? "90deg" : "-90deg")},
    rgba(250, 250, 250, 0) 0%,
    rgba(250, 250, 250, 1) 50%
  );
  position: absolute;
  right: ${(props) =>
    props.placement === "right" ? (props.visible ? 0 : "-50px") : "auto"};
  left: ${(props) =>
    props.placement === "left" ? (props.visible ? 0 : "-50px") : "auto"};
  top: 0;
  bottom: 0;
  align-items: center;
  justify-content: ${(props) =>
    props.placement === "right" ? "flex-end" : "flex-start"};
  svg {
    transform: rotate(
      ${(props) => (props.placement === "right" ? "-90deg" : "90deg")}
    );
  }
  transition: left 0.3s ease-out, right 0.3s ease-out;
`;

const NavArrowButton = styled(Flex)<FlexProps>`
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 36px;
  border: 1px solid ${(props) => themeGet("colors.ui.borderColor")(props)};
  border-radius: 4px;
  background-color: white;
  box-shadow: -1px 0px 4px rgba(0, 0, 0, 0.15);
`;

export const DesktopNav: React.FC<DesktopNavProps> = ({ shops }) => {
  const [leftArrowVisible, setLeftArrowVisible] = useState(false);
  const [rightArrowVisible, setRightArrowVisible] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const handleArrowClick = useCallback((side: "next" | "prev") => {
    if (scrollerRef.current) {
      const scrollDistance =
        side === "next"
          ? scrollerRef.current.scrollLeft +
            scrollerRef.current.clientWidth / SCROLL_INCREMENT
          : scrollerRef.current.scrollLeft -
            scrollerRef.current.clientWidth / SCROLL_INCREMENT;

      scrollTo({
        scrollerElement: scrollerRef.current,
        scrollLeftPosition: scrollDistance,
      });
    }
  }, []);

  const recordScrollPosition = () => {
    if (scrollerRef?.current) {
      localStorage.setItem(
        "navScrollPosition",
        `${scrollerRef.current.scrollLeft}`
      );
    }
  };

  const checkScrollPosition = (scroller: HTMLDivElement) => {
    if (
      scroller.scrollLeft + scroller.clientWidth >=
      scroller.scrollWidth - SCROLL_BUFFER
    ) {
      setRightArrowVisible(false);
    } else {
      setRightArrowVisible(true);
    }
    if (scroller.scrollLeft > SCROLL_BUFFER) {
      setLeftArrowVisible(true);
    } else {
      setLeftArrowVisible(false);
    }
  };

  useEffect(() => {
    const previousScrollPosition = localStorage.getItem("navScrollPosition");
    if (previousScrollPosition && scrollerRef?.current) {
      scrollTo({
        scrollerElement: scrollerRef.current,
        scrollLeftPosition: Number(previousScrollPosition),
      });
      localStorage.removeItem("navScrollPosition");
    }
  }, []);

  const onRefAssign = (scrollerElement: HTMLDivElement | null) => {
    scrollerRef.current = scrollerElement;
    if (scrollerElement) {
      const showNavArrows = () => checkScrollPosition(scrollerElement);
      showNavArrows();
      window.addEventListener("resize", showNavArrows);
    }
  };

  return (
    <>
      <MenuBar
        shops={shops}
        ref={onRefAssign}
        checkScrollPosition={checkScrollPosition}
        recordScrollPosition={recordScrollPosition}
      />
      <Hide lg xl>
        <ScrollIndicator
          aria-label="Scroll left"
          placement="left"
          visible={leftArrowVisible}
        >
          <NavArrowButton ml="10px" onClick={() => handleArrowClick("prev")}>
            <IconDownArrow width="17px" stroke="white" />
          </NavArrowButton>
        </ScrollIndicator>
        <ScrollIndicator
          aria-label="Scroll right"
          placement="right"
          visible={rightArrowVisible}
        >
          <NavArrowButton mr="10px" onClick={() => handleArrowClick("next")}>
            <IconDownArrow width="17px" stroke="white" />
          </NavArrowButton>
        </ScrollIndicator>
      </Hide>
    </>
  );
};
